<script setup lang="ts">
import type { Project } from '~/types/project'

const { data: projectDocs } = await useAsyncData('projects', () =>
  queryCollection('projects').all()
)

const projects = computed<Project[]>(() => (projectDocs.value ?? []) as unknown as Project[])

const sortedProjects = computed<Project[]>(() =>
  [...projects.value].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year
    return a.title.localeCompare(b.title)
  })
)

const years = computed(() => {
  const allYears = [...new Set(sortedProjects.value.map(p => p.year))].sort((a, b) => b - a)
  return ['All', ...allYears.map(String)]
})

const selectedYear = ref('All')

const filteredProjects = computed(() => {
  if (selectedYear.value === 'All') return sortedProjects.value
  return sortedProjects.value.filter(p => String(p.year) === selectedYear.value)
})

const pentastixProject = computed(() =>
  projects.value.find(p => (p.subProjects?.length ?? 0) > 0) ?? null
)

const modalOpen = ref(false)
const activeTab = ref('overview')

const VALID_KEYS = ['core', 'dashboard', 'frontend']
const HASH_RE = /^#pentastix(-([a-z]+))?$/

function openModal(tab = 'overview') {
  modalOpen.value = true
  activeTab.value = tab
}

watch(activeTab, (tab) => {
  if (!import.meta.client || !modalOpen.value) return
  const hash = tab === 'overview' ? '#pentastix' : `#pentastix-${tab}`
  history.replaceState(null, '', location.pathname + hash)
})

watch(modalOpen, (isOpen) => {
  if (!import.meta.client) return
  if (!isOpen) {
    activeTab.value = 'overview'
    history.replaceState(null, '', location.pathname)
  }
})

onMounted(() => {
  if (!import.meta.client) return
  const match = location.hash.match(HASH_RE)
  if (!match) return
  const key = match[2]
  const tab = key && VALID_KEYS.includes(key) ? key : 'overview'
  openModal(tab)
})
</script>

<template>
  <section
    id="projects"
    class="py-12"
  >
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-xs font-semibold uppercase tracking-widest text-muted">
        Projects
      </h2>

      <div
        v-if="years.length > 2"
        class="flex gap-1"
      >
        <button
          v-for="year in years"
          :key="year"
          class="px-2.5 py-1 text-xs rounded-lg transition-colors"
          :class="selectedYear === year
            ? 'bg-elevated text-highlighted border border-default'
            : 'text-muted hover:text-highlighted'"
          @click="selectedYear = year"
        >
          {{ year }}
        </button>
      </div>
    </div>

    <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <ProjectCard
        v-for="project in filteredProjects"
        :key="project.title"
        v-bind="project"
      />
    </div>

    <ProjectShowcaseModal
      v-if="pentastixProject"
      v-model="modalOpen"
      v-model:active-tab="activeTab"
      :project="pentastixProject"
    />
  </section>
</template>
